import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import ReactSlider from 'react-slider'
import styled from 'styled-components'
import Icon from 'components/Icon'
import BoxButton from 'components/BoxButton'
import { Button } from '@arnsbogroup/react-voicearchive'
import { useDropzone } from 'react-dropzone'

const ReactSliderWrapper = styled.div`
  display: block;
  height: 30px;

  .track-0 {
    background: #2b6cb0;
  }

  .track-1 {
    background: #cbd5e0;
  }
`

const Skills = ({ supplier: { status = null }, match, setCallback }) => {
  const [syncLevel, setSyncLevel] = useState(0)
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState({ description: false })
  const [newStudioImage, setNewStudioImage] = useState(null)
  const [newStudioImageUrl, setNewStudioImageUrl] = useState(null)
  const [uploadedStudioImageUrl, setUploadedStudioImageUrl] = useState(null)
  const { formatMessage } = useIntl()

  const syncLevels = [
    {
      id: null,
      key: 'none',
      name: formatMessage(messages.syncLevelNone),
      hint: formatMessage(messages.syncLevelNoneHint),
    },
    {
      id: 1,
      key: 'manuscript',
      name: formatMessage(messages.syncLevelManuscriptLabel),
      hint: formatMessage(messages.syncLevelManuscriptHint),
    },
    {
      id: 2,
      key: 'paragraph',
      name: formatMessage(messages.syncLevelParagraphLabel),
      hint: formatMessage(messages.syncLevelParagraphHint),
    },
    {
      id: 9,
      key: 'dubbing',
      name: formatMessage(messages.syncLevelDubbingLabel),
      hint: formatMessage(messages.syncLevelDubbingHint),
    }
  ]

  const [skills, setSkills] = useState({
    acting: {
      key: 'acting',
      value: 'ACTING',
      selected: false,
      icon: ['fal', 'theater-masks'],
      name: formatMessage(messages.acting),
      hint: formatMessage(messages.actingHint)
    },
    liveSession: {
      key: 'liveSession',
      value: 'LIVE_SESSION',
      selected: false,
      icon: ['fal', 'headset'],
      name: formatMessage(messages.liveSession),
      hint: formatMessage(messages.liveSessionHint)
    }
  })

  useEffect(() => {
    setCallback((event) => new Promise((resolve, reject) => {
      if (!description.length) {
        event.stopPropagation()
        event.preventDefault()

        setErrors({ description: true })

        return reject(formatMessage(messages.validationFailed))
      }

      if (!newStudioImage && !uploadedStudioImageUrl && !['ONLINE', 'ACTIVE'].includes(status)) {
        event.stopPropagation()
        event.preventDefault()

        setErrors({ studioImage: true })

        return reject(formatMessage(messages.validationStudioImageFailed))
      }

      fetch(`/rest/v2/supplier/${match.params.supplierId}`, {
        credentials: 'same-origin',
        method: 'PUT',
        body: JSON.stringify({
          live_session_notes: description,
          timing_level_uid: syncLevels[syncLevel].id,
          attributes: Object.values(skills)
          .filter(({ selected }) => selected)
          .map(({ value }) => value),
        }),
      })
        .then(resolve)
        .catch(reject)


      if (newStudioImage) {
        const formData = new FormData();

        formData.append('file', newStudioImage)
        formData.append('type', 6)

        fetch(`/rest/v2/supplier/${match.params.supplierId}/document`, {
          credentials: 'same-origin',
          method: 'POST',
          body: formData,
        })
        .then(resolve)
        .catch(reject);
      }
    }))
  }, [ description, syncLevel, skills, newStudioImage, uploadedStudioImageUrl])

  useEffect(() => {
    fetch(`/rest/v2/supplier/${match.params.supplierId}`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then(({
        live_session_notes,
        timing_level_uid,
        dob_year,
        attributes = [],
        studio_image
      }) => {
        setDescription(live_session_notes)

        if (timing_level_uid) {
          setSyncLevel(syncLevels.findIndex(({ id }) => id === parseInt(timing_level_uid)))
        }

        setSkills(Object.keys(skills).reduce((memo, key) => {
          memo[key].selected = attributes.includes(skills[key].value)
          return memo
        }, skills))

        setUploadedStudioImageUrl(studio_image)
      })
      .catch((error) => {
        window.alert(error)
      })
  }, [])

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setNewStudioImage(acceptedFiles[0])
      setUploadedStudioImageUrl(null)
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
    maxFiles: 1,
  });

  useEffect(() => {
    if (newStudioImage) {
      const url = URL.createObjectURL(newStudioImage)
      setNewStudioImageUrl(url)

      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [newStudioImage])

  return (
    <div>
      <div className="font-medium text-4xl">
        <FormattedMessage {...messages.title } />
      </div>

      <div className="text-gray-700 mt-6 w-2/3">
        <FormattedMessage {...messages.subtitle } />
      </div>

      <fieldset className="block mt-12">
        <p className="py-6">
          <FormattedMessage {...messages.syncLevelLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.syncLevelLabelHint } />
          </div>
        </p>
        <ReactSliderWrapper>
          <ReactSlider
            value={syncLevel}
            onChange={(syncLevel) => setSyncLevel(syncLevel)}
            min={0}
            max={3}
            thumbClassName="rounded-full border-2 border-primary bg-white w-5 h-5 cursor-pointer"
            trackClassName="h-1 bg-gray-400 my-2 track"
          />
        </ReactSliderWrapper>

        <div className="flex justify-between">
          { syncLevels.map(({ key, name, hint }, i) => (
            <div key={key} className={i === syncLevel ? 'text-primary' : 'text-gray-800'} title={hint}>
              <span className="cursor-pointer" onClick={() => setSyncLevel(i)}>{ name }</span>
              <Icon
                icon={['fal', 'question-circle']}
                className="cursor-pointer ml-1 hover:text-primary"
                onClick={() => window.alert(hint)}
              />
            </div>
          )) }
        </div>
      </fieldset>

      <fieldset className="block mt-12">
        <p className="py-6">
          <FormattedMessage {...messages.skillsLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.skillsLabelHint } />
          </div>
        </p>

        <div className="grid grid-cols-4 gap-8">
          { Object.values(skills).map(({ key, icon, selected, name, hint }) => (
            <BoxButton
              key={key}
              icon={icon}
              selected={selected}
              onClick={() => setSkills({ ...skills, [key]: { ...skills[key], selected: !skills[key].selected } })}
              title={hint}
            >
              { name }
              <Icon
                icon={['fal', 'question-circle']}
                className="cursor-pointer ml-1 hover:text-primary"
                onClick={(event) => {
                  event.stopPropagation()
                  window.alert(hint)
                }}
              />
            </BoxButton>
          )) }
        </div>
      </fieldset>

      <fieldset className="block mt-12">
        <p className="py-6">
          <FormattedMessage {...messages.descriptionLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.descriptionLabelHint } />
          </div>
        </p>

        <textarea
          value={description}
          onChange={({ target: { value } }) => {
            if (value.length < 501) {
              setDescription(value)
            }
          }}
          placeholder={formatMessage(messages.descriptionPlaceholder)}
          className={`w-full resize-y border p-4 focus:border-primary transition duration-200 ${errors.description ? 'border-red-600' : 'border-gray-400'}`}
          onBlur={() => setErrors((prevErrors) => ({ ...prevErrors, description: false }))}
        />
        <p className="text-xs py-2 font-light text-gray-600">
          <FormattedMessage {...messages.descriptionHint} values={{ left: 500 - description.length, max: 500 }} />
        </p>
      </fieldset>

      <fieldset className="block mt-12">
        <p className="py-6">
          <FormattedMessage {...messages.setupImageLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.setupImageLabelHint } />
          </div>
        </p>

        <div {...getRootProps()} className={`${errors.studioImage ? 'border-red-600' : 'border-dashed border-gray-300'} border-2 p-4`}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <p className='text-center'>Drop the image here...</p>
          ) : uploadedStudioImageUrl ? (
            <div>
              <FormattedMessage {...messages.setupImageLabel } />
              <img src={uploadedStudioImageUrl} alt="Preview" className="mt-2 max-w-full h-auto" />
            </div>
          ) : newStudioImage ? (
            <div>
              <p>{newStudioImage.name}</p>
              <img src={newStudioImageUrl} alt="Preview" className="mt-2 max-w-full h-auto" />
            </div>
          ) : (
            <FormattedMessage {...messages.setupImageNewUploadDescription } />
          )}
        </div>
      </fieldset>
    </div>
  )
}

export default Skills
